import { z } from "zod";
import { Base64, DeviceId, WrapCiphertext } from "./primitives.js";

/** Argon2id parameters stored alongside the salt so they can be tuned later. */
export const KdfParams = z.object({
  alg: z.literal("argon2id"),
  m_kib: z
    .int()
    .min(32 * 1024)
    .max(256 * 1024),
  t: z.int().min(2).max(10),
  p: z.int().min(1).max(4),
  salt: Base64,
});
export type KdfParams = z.infer<typeof KdfParams>;

export const DEFAULT_KDF = { alg: "argon2id", m_kib: 64 * 1024, t: 3, p: 1 } as const;

/**
 * The vault key wrapped for one unlock method. The server stores these but can
 * never open them. `ct` is base64(nonce || XChaCha20-Poly1305 ciphertext).
 */
export const WrappedVaultKey = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("password"), kdf: KdfParams, ct: WrapCiphertext }),
  z.object({ kind: z.literal("recovery"), ct: WrapCiphertext }),
  z.object({
    kind: z.literal("device"),
    device_id: DeviceId,
    /** Ephemeral X25519 public key used for the sealed box. */
    eph_pk: Base64.length(44),
    ct: WrapCiphertext,
  }),
  z.object({
    kind: z.literal("passkey"),
    credential_id: z.string().min(1).max(1400),
    /** Salt passed as the PRF `eval.first` input. */
    prf_salt: Base64,
    ct: WrapCiphertext,
  }),
]);
export type WrappedVaultKey = z.infer<typeof WrappedVaultKey>;
